<template>
	<v-container
		v-if="!isHiddenMetadataRow(metadatum)"
		class="d-flex align-center flex-nowrap pa-0 metadata-row"
	>
		<div v-if="!readOnly" class="ms-auto d-flex flex-nowrap">
			<v-metadata-hover-icon
				v-if="showApplyToAllRowOrColumnHeaders()"
				:disabled="metadatum.disableApplyToAllHeaders"
				:icon="applyToAllActive ? 'mdi-repeat' : 'mdi-repeat-off'"
				:color="applyToAllActive ? 'primary' : undefined"
				size="small"
				:tooltip="
					applyToAllActive
						? `Stop applying to all${
								headerType ? ' ' + headerType.toLowerCase() : ''
						  } headers`
						: `Apply to all${
								headerType ? ' ' + headerType.toLowerCase() : ''
						  } headers`
				"
				@click="toggleApplyToAll(metadatum)"
			/>
		</div>
		<div class="metadata-field-container flex-grow-1">
			<v-metadata-branch-value-field
				v-if="showKeyPicker(metadatum?.tag)"
				:model-value="metadatum"
				:composite-key-utils="compositeKeyUtils"
				:extraction-composite-keys="extractionCompositeKeys"
				:mode="mode"
				:read-only="isReadOnlyRow(metadatum)"
				@update:model-value="handleUpdate"
			/>
			<v-metadata-value-field
				v-else
				ref="valueField"
				:model-value="metadatum"
				:bounds="bounds"
				:direct-text-select="directTextSelect"
				:metadata="metadata"
				:mode="mode"
				:read-only="isReadOnlyRow(metadatum)"
				:select-text-function="selectTextFunction"
				:suggestion-map="suggestionMap"
				:tag-group="tagGroup"
				@update:model-value="handleUpdate"
				@update:location="handleLocationUpdate"
			/>
		</div>
		<div class="metadata-field-container flex-grow-1">
			<v-metadata-tag-select
				:disabled="isExternalMetadataRow(metadatum)"
				:model-value="metadatum"
				:is-graph-axis-value-tag="isGraphAxisValueTag(metadatum)"
				:limit-tag-groups="limitTagGroups"
				:mode="mode"
				:read-only="isReadOnlyRow(metadatum) || isGraphAxisValueTag(metadatum)"
				:tab="tab"
				:tags="getFilteredTags(metadatum.index, currentTagGroupTab)"
				@update:model-value="handleTagValueUpdate"
			/>
		</div>
		<div v-if="!readOnly" class="ms-auto d-flex flex-nowrap">
			<v-metadata-hover-icon
				:disabled="
					isReadOnlyRow(metadatum) ||
					mode === Mode.BLANK_TABLE_CELL ||
					isGraphAxisValueTag(metadatum)
				"
				:hidden="!showDeleteRowButton()"
				icon="mdi-delete-outline"
				size="small"
				tooltip="Delete row"
				@click="deleteRow"
			/>
			<v-metadata-hover-icon
				v-if="showCopyRowButton()"
				:disabled="
					isReadOnlyRow(metadatum) ||
					mode === Mode.BLANK_TABLE_CELL ||
					!metadatum?.tag?.uuid ||
					isKeyTag(metadatum?.tag) ||
					isGraphAxisValueTag(metadatum)
				"
				icon="mdi-content-duplicate"
				size="small"
				tooltip="Copy row to other tag groups"
				@click="copyRow($event)"
			/>
			<v-metadata-hover-icon
				:disabled="isReadOnlyRow(metadatum) || mode === Mode.BLANK_TABLE_CELL"
				:hidden="!showAddRowButton(metadatum)"
				icon="mdi-plus"
				size="small"
				tooltip="Add row"
				@click="addRow($event, tagGroup)"
			/>
		</div>
		<v-metadata-row-copy-toast
			:copy-feedback-open="copyFeedbackOpen"
			:copy-row-result="copyRowResult"
			:pointer-coords="pointerCoords"
		/>
	</v-container>
</template>

<script>
import VMetadataBranchValueField from "./VMetadataBranchValueField.vue";
import VMetadataRowCopyToast from "./VMetadataRowCopyToast.vue";
import VMetadataTagSelect from "./VMetadataTagSelect.vue";
import VMetadataValueField from "./VMetadataValueField.vue";
import VMetadataHoverIcon from "./VMetadataHoverIcon.vue";
import { Mode } from "./VMetadataConstants.js";

import _ from "lodash";

export default {
	name: "VMetadataRow",
	components: {
		VMetadataBranchValueField,
		VMetadataRowCopyToast,
		VMetadataTagSelect,
		VMetadataValueField,
		VMetadataHoverIcon,
	},

	inject: [
		"dialogKeydownHandler",
		"getFilteredTags",
		"isKeyTag",
		"isReferenceKeyTag",
		"showKeyPicker",
	],

	provide() {
		return {
			autoFocus: this.autoFocus,
			dialogKeydownHandler: this.dialogKeydownHandler,
			isKeyTag: this.isKeyTag,
			showKeyPicker: this.showKeyPicker,
		};
	},

	props: {
		bounds: { type: Object, default: null },
		compositeKeyUtils: { type: Object, default: null },
		extractionCompositeKeys: { type: Array, default: () => [] },
		copyRowResult: { type: Array, default: () => [] },
		currentTagGroupTab: { type: Number, default: 0 },
		directTextSelect: { type: Boolean, default: false },
		excludeKeys: { type: Array, default: () => [] },
		headerType: { type: String, default: null },
		index: { type: Number, default: 0 },
		indexTagMap: { type: Object, default: () => {} },
		initialCompositeKey: { type: String, default: null },
		limitTagGroups: { type: Array, default: () => [] },
		metadata: { type: Array, default: () => [] },
		metadatum: { type: Object, default: null },
		mode: { type: String, default: Mode.STANDARD },
		readOnly: { type: Boolean, default: false },
		selectTextFunction: { type: Function, default: null },
		suggestionMap: { type: Object, default: null },
		tab: { type: Object, default: null },
		tagGroup: { type: Object, default: null },
		tagGroupTabs: { type: Array, default: () => [] },
	},

	emits: [
		"add-row",
		"close",
		"copy-row",
		"delete-row",
		"refresh-height",
		"update:index-tag-map",
		"update:metadata",
		"update:metadatum",
		"update:location",
		"validate-values",
	],

	setup() {},

	data() {
		return {
			copyFeedbackOpen: false,
			Mode,
			pointerCoords: { x: 0, y: 0 },
		};
	},
	computed: {
		// Hello
		applyToAllActive() {
			return !!this.metadatum.applyToAllHeaders;
		},
	},

	methods: {
		addRow: function () {
			this.$emit("add-row");
		},
		autoFocus: function () {
			if (this.readOnly) {
				return false;
			}

			const filteredMetadata = this.metadata.filter(
				(data) => !isNaN(data.index) && !data.hidden,
			);
			const firstEmptyMetadatum = filteredMetadata.find(
				(metadatum) => metadatum.value === "",
			);
			if (firstEmptyMetadatum) {
				return this.metadatum.index === firstEmptyMetadatum.index;
			}

			const lastFilledMetadatum = filteredMetadata
				.reverse()
				.find((metadatum) => !!metadatum.value);

			if (lastFilledMetadatum) {
				return this.metadatum.index === lastFilledMetadatum.index;
			}

			return false;
		},
		copyRow(evt) {
			if (evt && evt.clientX && evt.clientY) {
				this.pointerCoords = { x: evt.clientX, y: evt.clientY };
			}
			this.copyFeedbackOpen = true;
			setTimeout(() => {
				this.copyFeedbackOpen = false;
			}, 2000);
			this.$emit("copy-row", this.index);
		},
		deleteRow() {
			this.$emit("delete-row", this.index);
		},
		isExternalMetadataRow(metadatum) {
			if (_.isEmpty(this.tagGroupTabs)) {
				return false;
			}

			const currentTagGroup = this.tagGroupTabs[this.currentTagGroupTab];
			return metadatum.tagGroup?.uuid !== currentTagGroup?.uuid;
		},
		isHiddenMetadataRow(metadatum) {
			return metadatum.hidden || this.excludeKeys.includes(metadatum.index);
		},
		isReadOnlyRow(metadatum) {
			return this.readOnly || this.isExternalMetadataRow(metadatum);
		},
		isGraphAxisValueTag(metadatum) {
			return !!(
				metadatum.axis &&
				metadatum.axisType &&
				metadatum.axisType === "value"
			);
		},
		showAddRowButton(metadatum) {
			if (this.mode === Mode.BRANCH_EDIT || this.mode === Mode.BRANCH_SELECT)
				return false;

			const maxVisibleIndex = this.metadata
				.filter((data) => !isNaN(data.index) && !data.hidden)
				.filter(
					(data) =>
						_.isEmpty(this.tagGroupTabs) ||
						data.tagGroup?.uuid ===
							this.tagGroupTabs[this.currentTagGroupTab].uuid,
				)
				.map((data) => parseInt(data.index, 10))
				.reduce((max, current) => Math.max(max, current), -Infinity);

			return parseInt(metadatum.index, 10) === maxVisibleIndex;
		},
		showDeleteRowButton() {
			if (
				[Mode.BRANCH_EDIT, Mode.BRANCH_SELECT].includes(this.mode) &&
				this.initialCompositeKey
			) {
				return false;
			}

			return true;
		},
		showCopyRowButton() {
			if (
				[Mode.TABLE_CELL, Mode.BLANK_TABLE_CELL, Mode.TABLE_HEADER].includes(
					this.mode,
				) &&
				this.tagGroupTabs.length > 1
			) {
				return true;
			}

			return false;
		},
		showApplyToAllRowOrColumnHeaders() {
			return (
				this.mode === Mode.TABLE_HEADER || this.mode === Mode.BAR_GRAPH_HEADER
			);
		},
		toggleApplyToAll(metadatum) {
			metadatum.applyToAllHeaders = !metadatum.applyToAllHeaders;
		},
		handleTagValueUpdate(metadatum) {
			const index = metadatum.index;
			const existingTag = this.indexTagMap[index];

			const wasKeyPicker = this.showKeyPicker(existingTag);
			const isKeyPicker = this.showKeyPicker(metadatum?.tag);

			if (
				existingTag?.uuid !== metadatum.tag?.uuid &&
				(wasKeyPicker !== isKeyPicker || (wasKeyPicker && isKeyPicker))
			) {
				metadatum.value = null;
			}

			this.$emit("update:index-tag-map", {
				...this.indexTagMap,
				[index]: metadatum.tag,
			});

			this.$emit("update:metadatum", metadatum);
		},
		handleUpdate(metadatum) {
			this.$emit("update:metadatum", metadatum);
		},
		handleLocationUpdate(location) {
			this.$emit("update:location", location);
		},
	},
};
</script>

<style lang="scss">
.metadata-field-container {
	flex-basis: 0;
}
.metadata-row {
	position: relative;
	overflow: visible;
}
</style>
